// smartSearch.js — hearing the menu the way people say it.
//
// Two things live here, because they only matter together:
//
//   1. VOICE  - the browser's own speech recognition, wrapped in a hook.
//   2. FUZZY  - matching what was heard against what the menu is called.
//
// The second exists because the first is never exact. A waiter says "buni
// rayta" and the browser writes "Buni Rayta"; the menu says "Bundi Raita".
// A plain `includes()` finds nothing, and the screen looks broken to the one
// person who did everything right. Typing has the same problem in this
// country's menus: panir/paneer, chole/chhole, dahi/dhai, tikka/tika.
//
// Nothing is sent anywhere: the browser turns speech into text on the device,
// and the matching below is arithmetic on strings.
import { useCallback, useRef, useState } from 'react';
import { toast } from 'react-toastify';

/* ─────────────────────────── 1. Phonetic key ───────────────────────────── */
//
// A spelling-insensitive key for one word. Two words that SOUND the same in
// Hinglish should produce the same key, so the comparison below is about the
// sound, not the spelling somebody happened to type into the item master.
//
// The rules are ordered: aspirated pairs first (they are two letters), then
// the letters that trade places in transliteration, then the vowels - which
// are where nearly all the variation is, so every non-initial one goes.
//
//   bundi  → b-n-d  → "bnd"
//   buni   → b-n    → "bn"      (distance 1 from "bnd" → still a match)
//   raita  → r-t    → "rt"
//   rayta  → r-t    → "rt"      (exact)
//   paneer → p-n-r  → "pnr"
//   panir  → p-n-r  → "pnr"     (exact)
export function phoneticKey(word) {
  let s = String(word || '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
  if (!s) return '';
  if (/^\d+$/.test(s)) return s; // "65" in "Chicken 65" is not a sound

  s = s
    // Aspirated consonants: the h is a breath, not a letter people agree on.
    .replace(/chh/g, 'ch')
    .replace(/([kgtdbp])h/g, '$1')
    .replace(/ph/g, 'f')
    .replace(/sh/g, 's')
    .replace(/gh/g, 'g')
    // Letters that swap in transliteration.
    .replace(/z/g, 'j')
    .replace(/v/g, 'w')
    .replace(/q/g, 'k')
    .replace(/x/g, 'ks')
    .replace(/c(?=[^h])/g, 'k') // "cutlet" → k; "chana" keeps its ch
    .replace(/c$/g, 'k')
    // y between two consonants is doing a vowel's job: rayta = raita.
    .replace(/([^aeiou])y([^aeiou])/g, '$1i$2');

  const first = s[0];
  // Every vowel after the first letter goes, and y counts as one - it is a
  // vowel in every transliteration that matters here (rayta = raita, biryani
  // = biriyani). What is left is the consonant skeleton, which is what
  // survives a different speller - or a phone's guess at an accent.
  const rest = s.slice(1).replace(/[aeiouy]/g, '');
  let key = (first + rest).replace(/(.)\1+/g, '$1'); // no doubled letters
  return key;
}

// Spoken numbers are written as words; menus write them as digits. "Chicken
// sixty five" has to reach "Chicken 65", so the words become digits before
// anything else looks at them - including "sixty five" as one number.
const ONES = {
  zero: 0, one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7,
  eight: 8, nine: 9, ten: 10, eleven: 11, twelve: 12, thirteen: 13,
  fourteen: 14, fifteen: 15, sixteen: 16, seventeen: 17, eighteen: 18,
  nineteen: 19,
};
const TENS = {
  twenty: 20, thirty: 30, forty: 40, fourty: 40, fifty: 50, sixty: 60,
  seventy: 70, eighty: 80, ninety: 90,
};
export function numeralize(text) {
  const words = String(text || '').toLowerCase().split(/\s+/);
  const out = [];
  for (let i = 0; i < words.length; i++) {
    const w = words[i].replace(/[^a-z0-9]/g, '');
    if (TENS[w] !== undefined) {
      const next = (words[i + 1] || '').replace(/[^a-z]/g, '');
      if (ONES[next] !== undefined && ONES[next] < 10) {
        out.push(String(TENS[w] + ONES[next]));
        i++;
        continue;
      }
      out.push(String(TENS[w]));
      continue;
    }
    if (ONES[w] !== undefined) {
      out.push(String(ONES[w]));
      continue;
    }
    out.push(words[i]);
  }
  return out.join(' ');
}

// The whole phrase as keys, one per word, blanks dropped.
export function phoneticTokens(text) {
  return numeralize(text)
    .split(/[\s\-_/,()]+/)
    .map(phoneticKey)
    .filter(Boolean);
}

/* ─────────────────────────── 2. Edit distance ──────────────────────────── */
// Levenshtein, capped: once the best possible result is worse than `max`
// there is no reason to finish the table.
function distance(a, b, max = 2) {
  if (a === b) return 0;
  if (Math.abs(a.length - b.length) > max) return max + 1;
  const prev = new Array(b.length + 1);
  const cur = new Array(b.length + 1);
  for (let j = 0; j <= b.length; j++) prev[j] = j;
  for (let i = 1; i <= a.length; i++) {
    cur[0] = i;
    let best = cur[0];
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      cur[j] = Math.min(cur[j - 1] + 1, prev[j] + 1, prev[j - 1] + cost);
      if (cur[j] < best) best = cur[j];
    }
    if (best > max) return max + 1;
    for (let j = 0; j <= b.length; j++) prev[j] = cur[j];
  }
  return prev[b.length];
}

// How wrong a key of this length may be and still be the same word. A short
// key has no room for error ("bn" vs "bt" are different dishes); a long one
// can lose a syllable to a phone's microphone.
function allowedFor(len) {
  if (len <= 2) return 0;
  if (len <= 4) return 1;
  return 2;
}

/* ─────────────────────────── 3. Scoring ────────────────────────────────── */
//
// One query word against one menu word: 1 for the same sound, less as the
// match gets looser. Returns 0 when they are not the same word at all.
function tokenScore(q, t) {
  if (!q || !t) return 0;
  if (q === t) return 1;
  if (t.startsWith(q) && q.length >= 2) return 0.85;
  if (t.includes(q) && q.length >= 3) return 0.7;
  const d = distance(q, t, allowedFor(Math.max(q.length, t.length)));
  const allowed = allowedFor(Math.max(q.length, t.length));
  if (d <= allowed) return 0.8 - d * 0.15;
  return 0;
}

/**
 * How well a spoken/typed phrase matches one piece of text. 0 = not a match.
 *
 * Every word of the query has to find a home in the text, so "paneer tikka"
 * does not match plain "Paneer" as strongly as it matches "Paneer Tikka",
 * and a single stray word cannot drag an unrelated dish into the results.
 */
export function fuzzyScore(query, text) {
  const qs = phoneticTokens(query);
  const ts = phoneticTokens(text);
  if (!qs.length || !ts.length) return 0;

  let total = 0;
  for (const q of qs) {
    let best = 0;
    for (const t of ts) {
      const s = tokenScore(q, t);
      if (s > best) best = s;
    }
    if (best === 0) return 0; // a word nobody said - not this dish
    total += best;
  }
  let score = total / qs.length;
  // Every word of the name was accounted for: an exact phrase, not a phrase
  // that happens to be inside a longer name.
  if (qs.length === ts.length) score += 0.05;
  return Math.min(1, score);
}

/**
 * Filter a list the phonetic way, best match first.
 *
 * @param list      the items
 * @param query     what was typed or heard
 * @param getText   item -> the text to match (name, and anything else useful)
 * @param min       how good a match has to be to show at all
 */
export function fuzzyFilter(list, query, getText, { min = 0.6, limit = 0 } = {}) {
  const q = String(query || '').trim();
  if (!q) return [];
  const scored = [];
  for (const item of list || []) {
    const score = fuzzyScore(q, getText(item));
    if (score >= min) scored.push({ item, score });
  }
  scored.sort((a, b) => b.score - a.score);
  const out = scored.map((s) => s.item);
  return limit > 0 ? out.slice(0, limit) : out;
}

/* ─────────────────────────── 4. Voice ──────────────────────────────────── */
export const voiceSupported = () =>
  typeof window !== 'undefined' &&
  !!(window.SpeechRecognition || window.webkitSpeechRecognition);

/**
 * The mic button's behaviour, in one place.
 *
 * onResult(text) fires as the words arrive, so the list narrows while the
 * waiter is still speaking. A second tap stops it; it also stops by itself
 * when the speaking does.
 */
export function useVoiceSearch(onResult, { lang = 'en-IN', onStart } = {}) {
  const [listening, setListening] = useState(false);
  const recRef = useRef(null);
  const resultRef = useRef(onResult);
  resultRef.current = onResult;

  const toggle = useCallback(() => {
    if (listening) {
      try {
        recRef.current?.stop();
      } catch {
        /* already stopped */
      }
      return;
    }
    const Speech = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!Speech) {
      toast.info('Voice search needs Chrome or the Android app browser.');
      return;
    }
    try {
      const rec = new Speech();
      // Indian English hears the menu as it is written; the device's own
      // language is the fallback the browser applies anyway.
      rec.lang = lang;
      rec.interimResults = true;
      rec.continuous = false;
      rec.maxAlternatives = 1;
      rec.onstart = () => {
        setListening(true);
        onStart?.();
      };
      rec.onresult = (e) => {
        const text = Array.from(e.results)
          .map((r) => r[0]?.transcript || '')
          .join(' ')
          .trim();
        if (text) resultRef.current?.(text);
      };
      rec.onerror = (ev) => {
        setListening(false);
        if (ev?.error === 'not-allowed') {
          toast.error('Microphone is blocked. Allow the mic for this site and try again.');
        } else if (ev?.error !== 'aborted' && ev?.error !== 'no-speech') {
          toast.error('Could not hear that. Try again.');
        }
      };
      rec.onend = () => {
        setListening(false);
        recRef.current = null;
      };
      recRef.current = rec;
      rec.start();
    } catch {
      setListening(false);
      toast.error('Voice search could not start.');
    }
  }, [lang, listening, onStart]);

  return { supported: voiceSupported(), listening, toggle };
}
